import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Pin, Plus, Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ProjectDTO } from '@harness/shared';
import TopBar, { SearchButton } from '../components/TopBar';
import { Micro, Seg, Spinner, StatusDot, statusLabel, SwitchRow } from '../components/ui';
import { api } from '../lib/api';
import { relTime } from '../lib/format';
import { qk } from '../lib/keys';
import { useProjects, useSession } from '../lib/queries';

type Sort = 'recent' | 'name' | 'created';

export default function Projects() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const { data: projects, isLoading } = useProjects();
  // Every member creates their own (private) projects.
  const isOwner = !!useSession().data?.user?.organisation;
  const [q, setQ] = useState('');
  const [sort, setSort] = useState<Sort>('recent');
  const [tag, setTag] = useState('all');
  const [showArchived, setShowArchived] = useState(false);

  const pin = useMutation({
    mutationFn: (p: ProjectDTO) => api.patch<ProjectDTO>(`/api/projects/${p.id}`, { pinned: !p.pinned }),
    onMutate: (p) => qc.setQueryData<ProjectDTO[]>(qk.projects, (l) => l?.map((x) => (x.id === p.id ? { ...x, pinned: !p.pinned } : x))),
  });

  const all = projects ?? [];
  const tags = useMemo(() => ['all', ...Array.from(new Set(all.flatMap((p) => p.tags)))], [all]);
  const list = all
    .filter((p) => (showArchived || !p.archived) && (tag === 'all' || p.tags.includes(tag)) && (!q || `${p.name} ${p.description}`.toLowerCase().includes(q.toLowerCase())))
    .sort(
      (a, b) =>
        Number(b.pinned) - Number(a.pinned) ||
        (sort === 'name' ? a.name.localeCompare(b.name) : sort === 'created' ? b.createdAt.localeCompare(a.createdAt) : b.lastActiveAt.localeCompare(a.lastActiveAt)),
    );
  const active = all.filter((p) => !p.archived);
  const online = active.filter((p) => p.server?.status === 'online').length;

  return (
    <div className="screen">
      <TopBar left={<SearchButton />} />
      <main className="screen-body">
        <div className="projects-head">
          <div style={{ marginRight: 'auto' }}>
            <h1 className="projects-title">Projects</h1>
            <div className="muted" style={{ marginTop: 4 }}>
              {active.length} project{active.length === 1 ? '' : 's'} · {online} server{online === 1 ? '' : 's'} online
            </div>
          </div>
          {isOwner && (
            <button className="btn btn-primary btn-lg" onClick={() => nav('/new')}>
              <Plus className="icon" style={{ fontSize: 16 }} />
              New project
            </button>
          )}
        </div>

        <div className="filter-row">
          <div className="filter-group">
            <Micro>Search</Micro>
            <div className="input-icon" style={{ width: 240, maxWidth: '100%' }}>
              <Search className="icon" />
              <input className="input" placeholder="Filter by name" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Filter projects" />
            </div>
          </div>
          <div className="filter-group">
            <Micro>Sort by</Micro>
            <Seg<Sort>
              value={sort}
              onChange={setSort}
              options={[
                { value: 'recent', label: 'Last used' },
                { value: 'name', label: 'Name' },
                { value: 'created', label: 'Created' },
              ]}
            />
          </div>
          <div className="filter-group">
            <Micro>Tags</Micro>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', minHeight: 40, alignItems: 'center' }}>
              {tags.map((t) => (
                <button key={t} className="chip" aria-pressed={tag === t} onClick={() => setTag(t)}>
                  {t === 'all' ? 'All tags' : t}
                </button>
              ))}
            </div>
          </div>
          <div style={{ marginLeft: 'auto' }}>
            <SwitchRow on={showArchived} onChange={setShowArchived}>
              Show archived
            </SwitchRow>
          </div>
        </div>

        {isLoading ? (
          <div style={{ padding: 40 }}>
            <Spinner />
          </div>
        ) : all.length === 0 ? (
          <div style={{ padding: '8px 24px 40px' }}>
            <div className="success-banner">
              <span className="success-icon">
                <Plus className="icon" />
              </span>
              <div style={{ flex: 1 }}>
                <div className="title-900" style={{ fontSize: 20 }}>
                  Create your first project
                </div>
                <div className="muted">Connect its server and pick a model. Each project gets its own agents, memory and history.</div>
              </div>
              {isOwner ? (
                <button className="btn btn-primary" onClick={() => nav('/new')}>
                  <Plus className="icon" />
                  New project
                </button>
              ) : (
                <div className="muted">Your account isn't set up yet. Ask an admin to add you.</div>
              )}
            </div>
          </div>
        ) : (
          <div className="project-grid">
            {list.map((p) => (
              <div
                key={p.id}
                role="link"
                tabIndex={0}
                className={`card project-card ${p.archived ? 'archived' : ''}`}
                onClick={() => nav(`/p/${p.slug}`)}
                onKeyDown={(e) => e.key === 'Enter' && nav(`/p/${p.slug}`)}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                  <div className="initial-tile" style={p.color ? { background: p.color, color: '#fff' } : undefined}>
                    {p.name[0]?.toUpperCase()}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Micro>{p.tags.join(' · ') || 'project'}</Micro>
                    <div className="project-name">{p.name}</div>
                  </div>
                  {p.runningTurns > 0 && <Spinner className="accent-text" />}
                  <button
                    className={`pin-btn ${p.pinned ? 'on' : ''}`}
                    title={p.pinned ? 'Unpin' : 'Pin'}
                    aria-pressed={p.pinned}
                    onClick={(e) => {
                      e.stopPropagation();
                      pin.mutate(p);
                    }}
                  >
                    <Pin className="icon" />
                  </button>
                </div>
                <div className="muted" style={{ flex: 1, textWrap: 'pretty' }}>
                  {p.description || <span className="faint">No description</span>}
                </div>
                <div className="card-meta">
                  <div>
                    <Micro>Server</Micro>
                    <div className="v">
                      {p.server ? (
                        <>
                          <StatusDot status={p.server.status} />
                          {statusLabel(p.server.status)}
                        </>
                      ) : (
                        <span className="faint">None</span>
                      )}
                    </div>
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <Micro>Model</Micro>
                    <div className="v ellipsis" style={{ display: 'block' }}>
                      {p.defaultModel ?? <span className="faint">Not set</span>}
                    </div>
                  </div>
                  <div>
                    <Micro>Active</Micro>
                    <div className="v">{relTime(p.lastActiveAt)}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        {!isLoading && all.length > 0 && list.length === 0 && (
          <div style={{ padding: '0 24px 40px' }} className="muted">
            No projects match.{' '}
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                setQ('');
                setTag('all');
              }}
            >
              Clear filters
            </a>
          </div>
        )}
      </main>
    </div>
  );
}
